if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.currentColorIndex = 0;
      }

      connectedCallback() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.productId = this.dataset.productId;

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.onThumbnailClick.bind(this, mediaToSwitch));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Force thumbnail strip to start at first item — overrides any browser scroll restoration
        const thumbnailSlider = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailSlider) {
          thumbnailSlider.scrollLeft = 0;
          requestAnimationFrame(() => { thumbnailSlider.scrollLeft = 0; });
        }

        // Activate second media item on load — runs after all slider init completes
        window.setTimeout(() => {
          const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
          if (mediaItems.length >= 2) {
            const secondId = mediaItems[1].dataset.mediaId;
            if (secondId) this.setActiveMedia(secondId, false);
          }
        }, 100);

        // Listen for color swatch changes
        this.setupColorSwatchListeners();
      }

      setupColorSwatchListeners() {
        // Listen for custom color change events
        document.addEventListener('color-swatch:change', (event) => {
          if (event.detail && event.detail.productId === this.productId) {
            this.updateGalleryForColor(event.detail.colorIndex, event.detail.colorName);
          }
        });
      }

      updateGalleryForColor(colorIndex, colorName) {
        // PHASE 5: GALLERY LOGIC REPLACEMENT - FIXED
        const hasColorVariants = this.dataset.hasColorVariants === 'true';
        if (!hasColorVariants) return;

        const colorMediaMap = window.COLOR_MEDIA_MAP?.[this.productId];
        if (!colorMediaMap || !Array.isArray(colorMediaMap) || colorMediaMap.length === 0) return;

        if (colorIndex < 0 || colorIndex >= colorMediaMap.length) return;

        const colorData = colorMediaMap[colorIndex];
        if (!colorData) return;

        const mediaList = colorData.variantMedia;
        if (!mediaList || !Array.isArray(mediaList) || mediaList.length === 0) return;

        this.currentColorIndex = colorIndex;
        this.replaceGalleryMedia(mediaList);
      }

      replaceGalleryMedia(mediaList) {
        const sliderGallery = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        const thumbnailSlider = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        
        if (!sliderGallery || !mediaList || !Array.isArray(mediaList) || mediaList.length === 0) return;

        // Use DocumentFragment for better performance
        const galleryFragment = document.createDocumentFragment();
        const thumbnailFragment = document.createDocumentFragment();

        const sectionId = this.id.replace('MediaGallery-', '');
        
        // SMART GRID: Update media count for conditional layout
        sliderGallery.setAttribute('data-media-count', mediaList.length);

        // Inject new media items
        mediaList.forEach((media, index) => {
          if (!media || !media.id) return;

          const isFirst = index === 0;
          
          // Create main gallery slide
          const slide = this.createGallerySlide(media, sectionId, index, isFirst);
          if (slide) galleryFragment.appendChild(slide);

          // Create thumbnail
          if (thumbnailSlider) {
            const thumbnail = this.createThumbnail(media, sectionId, index, isFirst);
            if (thumbnail) thumbnailFragment.appendChild(thumbnail);
          }
        });

        // Batch DOM updates
        sliderGallery.innerHTML = '';
        sliderGallery.appendChild(galleryFragment);
        
        if (thumbnailSlider) {
          thumbnailSlider.innerHTML = '';
          thumbnailSlider.appendChild(thumbnailFragment);
        }

        // Re-initialize slider
        const sliderComponent = this.elements.viewer.closest('slider-component');
        if (sliderComponent && sliderComponent.resetPages) {
          sliderComponent.resetPages();
        }

        const thumbnailSliderComponent = this.elements.thumbnails?.closest('slider-component');
        if (thumbnailSliderComponent && thumbnailSliderComponent.resetPages) {
          thumbnailSliderComponent.resetPages();
        }

        // Reattach thumbnail click listeners
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            // Remove old listeners by cloning
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            newButton.addEventListener('click', this.onThumbnailClick.bind(this, mediaToSwitch));
          }
        });

        // Set first media as active
        if (mediaList.length > 0 && mediaList[0].id) {
          this.setActiveMedia(`${sectionId}-${mediaList[0].id}`, false);
        }

        // Update counter
        const counterTotal = this.querySelector('.slider-counter--total');
        if (counterTotal) {
          counterTotal.textContent = mediaList.length;
        }

        const counterCurrent = this.querySelector('.slider-counter--current');
        if (counterCurrent) {
          counterCurrent.textContent = '1';
        }
      }

      createGallerySlide(media, sectionId, index, isFirst) {
        const li = document.createElement('li');
        li.id = `Slide-${sectionId}-${media.id}`;
        li.className = `product__media-item grid__item slider__slide${isFirst ? ' is-active' : ''}${media.type !== 'image' ? ' product__media-item--full' : ''}`;
        li.setAttribute('data-media-id', `${sectionId}-${media.id}`);
        li.setAttribute('data-product-media', 'true');
        li.setAttribute('data-color-media', 'true');

        // Create media content based on type
        if (media.type === 'image') {
          const img = document.createElement('img');
          img.src = media.previewSrc || media.src;
          img.alt = media.alt || '';
          img.loading = index === 0 ? 'eager' : 'lazy';
          img.width = media.width;
          img.height = media.height;
          img.className = 'motion-reduce';
          
          const modalOpener = document.createElement('modal-opener');
          modalOpener.className = 'product__modal-opener product__modal-opener--image';
          modalOpener.setAttribute('data-modal', `#ProductModal-${sectionId}`);
          
          const button = document.createElement('button');
          button.className = 'product__media-toggle';
          button.type = 'button';
          button.setAttribute('aria-label', 'Open media in modal');
          
          button.appendChild(img);
          modalOpener.appendChild(button);
          li.appendChild(modalOpener);
        } else if (media.type === 'video' || media.type === 'external_video') {
          // For videos, create a deferred media element
          const deferredMedia = document.createElement('deferred-media');
          deferredMedia.className = 'product__media-item--video';
          
          const img = document.createElement('img');
          img.src = media.previewSrc || media.src;
          img.alt = media.alt || '';
          img.loading = 'lazy';
          
          deferredMedia.appendChild(img);
          li.appendChild(deferredMedia);
        }

        return li;
      }

      createThumbnail(media, sectionId, index, isFirst) {
        const li = document.createElement('li');
        li.id = `Slide-Thumbnails-${sectionId}-${media.id}`;
        li.className = 'thumbnail-list__item slider__slide';
        li.setAttribute('data-target', `${sectionId}-${media.id}`);
        li.setAttribute('data-media-position', index + 1);
        li.setAttribute('data-color-media', 'true');

        const button = document.createElement('button');
        button.className = 'thumbnail global-media-settings global-media-settings--no-shadow';
        button.setAttribute('aria-label', `Load image ${index + 1}`);
        button.setAttribute('aria-controls', `GalleryViewer-${sectionId}`);
        if (isFirst) {
          button.setAttribute('aria-current', 'true');
        }

        const img = document.createElement('img');
        img.src = media.previewSrc || media.src;
        img.alt = media.alt || '';
        img.loading = 'lazy';
        img.width = 416;
        img.height = Math.round(416 / (media.aspectRatio || 1));

        button.appendChild(img);
        li.appendChild(button);

        return li;
      }

      onThumbnailClick(mediaToSwitch, event) {
        event.preventDefault();
        event.stopPropagation();
        
        const targetId = mediaToSwitch.dataset.target;
        if (!targetId) return;
        
        // Directly set active media using media ID (bypasses index-based logic)
        this.setActiveMedia(targetId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        
        // Batch class updates for better performance
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          if (element !== activeMedia) {
            element.classList.remove('is-active');
          }
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}